<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle">
    <div class="c-tabs__inner">
      <div class="c-tabs__nav-wrap">
        <div class="c-tabs__nav" role="tablist" aria-label="Tabs navigation">
          <button
            v-for="(tab, index) in normalizedTabs"
            :id="tabButtonId(index)"
            :key="tab.id"
            class="c-tabs__tab"
            :class="{ 'is-active': activeIndex === index }"
            type="button"
            role="tab"
            :aria-selected="activeIndex === index"
            :aria-controls="tabPanelId(index)"
            @click="setActiveIndex(index)"
            @keydown.left.prevent="focusTab(index - 1)"
            @keydown.right.prevent="focusTab(index + 1)"
            @keydown.home.prevent="focusTab(0)"
            @keydown.end.prevent="focusTab(normalizedTabs.length - 1)"
          >
            <span class="c-tabs__tab-bg" aria-hidden="true"></span>
            <span class="c-tabs__tab-text">{{ tab.title }}</span>
          </button>
        </div>
      </div>

      <div
        v-if="hasPlaceholderContent || activeComponents.length"
        :id="tabPanelId(activeIndex)"
        class="c-tabs__panel"
        role="tabpanel"
        :aria-labelledby="tabButtonId(activeIndex)"
      >
        <Placeholder
          v-if="hasPlaceholderContent"
          :key="selectedPlaceholderKey"
          name="jss-tabs"
          :rendering="selectedPlaceholderRendering"
        />
        <component
          :is="resolveDynamicComponent(componentData)"
          v-for="(componentData, componentIndex) in activeComponents"
          v-else
          v-bind="getDynamicComponentProps(componentData)"
          :key="componentData?.uid ?? componentData?.id ?? `${activeIndex}-${componentIndex}`"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Placeholder } from '@sitecore-jss/sitecore-jss-vue';
import type { ComponentRendering } from '@sitecore-jss/sitecore-jss-vue';
import type { TabsProps, TabsItemFields } from '@/types/components/tabs';
import type { ScItem } from '@/types/sitecore';
import componentFactory from '@/temp/componentFactory';
import utils from '@/utils';

type DynamicComponentItem = {
  uid?: string;
  dataSource?: string;
  id?: string;
  name?: string;
  displayName?: string;
  componentName?: string;
  template?: string;
  templateName?: string;
  fields?: Record<string, unknown> | null;
  placeholders?: Record<string, ComponentRendering[]>;
};

interface NormalizedTab {
  id: string;
  title: string;
  components: DynamicComponentItem[];
}

const props = defineProps<TabsProps>();
const activeIndex = ref(0);
let autoSwitchTimer: ReturnType<typeof setInterval> | null = null;

const rootClass = computed(() => {
  return utils.overall.baseComClass(props.fields, 'c-tabs');
});

const rootStyle = computed(() => {
  return utils.overall.baseComStyle(props.fields);
});
const fields = computed(() => props.fields);
const autoSwitchInterval = computed(() => {
  const seconds = Number(fields.value?.autoSwitchInterval?.value);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 1000;
});

const configuredTabs = computed<ScItem<TabsItemFields>[]>(() => {
  const tabs = utils.sc.getFieldObj(fields.value, 'tabs');
  return Array.isArray(tabs) ? tabs : [];
});

const placeholderRenderings = computed<ComponentRendering[]>(() => {
  const placeholders = props.rendering?.placeholders as
    Record<string, ComponentRendering[]> | undefined;
  const renderings = placeholders?.['jss-tabs'];
  return Array.isArray(renderings) ? renderings : [];
});

const normalizedTabs = computed<NormalizedTab[]>(() => {
  const length = Math.max(configuredTabs.value.length, placeholderRenderings.value.length);

  return Array.from({ length }, (_, index) => {
    const tabItem = configuredTabs.value[index];
    const childRendering = placeholderRenderings.value[index] as any;
    const tabFields = tabItem?.fields;
    const childFields = childRendering?.fields;
    const title =
      tabFields?.title?.value ||
      childFields?.tabTitle?.value ||
      childFields?.title?.value ||
      childRendering?.name ||
      childRendering?.componentName ||
      `Tab ${index + 1}`;

    return {
      id: tabItem?.id || childRendering?.uid || childRendering?.dataSource || `tabs-${index}`,
      title,
      components: (tabFields?.components ?? []).map((componentData) => ({
        ...componentData,
        componentName: getComponentName(componentData),
      })),
    };
  });
});

const activeComponents = computed(() => normalizedTabs.value[activeIndex.value]?.components ?? []);

const hasPlaceholderContent = computed(() => placeholderRenderings.value.length > 0);

const selectedPlaceholderRendering = computed<ComponentRendering>(() => {
  const currentRendering = placeholderRenderings.value[activeIndex.value];

  return {
    ...props.rendering,
    placeholders: {
      ...(props.rendering?.placeholders || {}),
      'jss-tabs': currentRendering ? [currentRendering] : [],
    },
  } as ComponentRendering;
});

const selectedPlaceholderKey = computed(() => {
  const currentRendering = placeholderRenderings.value[activeIndex.value] as any;
  return currentRendering?.uid || currentRendering?.dataSource || `tabs-panel-${activeIndex.value}`;
});

function getComponentName(componentData?: DynamicComponentItem) {
  return `${
    componentData?.componentName ||
    componentData?.templateName ||
    componentData?.template ||
    'ImageTextCard'
  }`.replace(/\s+/g, '');
}

function resolveDynamicComponent(componentData?: DynamicComponentItem) {
  const componentName = getComponentName(componentData);
  return componentFactory(componentName) || componentName;
}

function getDynamicComponentProps(componentData: DynamicComponentItem) {
  return {
    fields: componentData?.fields ?? {},
    rendering: componentData as ComponentRendering,
  };
}

const instanceId = computed(() =>
  String(props.rendering?.uid || props.dataSource || 'tabs').replace(/[^a-zA-Z0-9_-]/g, '')
);

const tabButtonId = (index: number) => `${instanceId.value}-tab-${index}`;
const tabPanelId = (index: number) => `${instanceId.value}-panel-${index}`;

const setActiveIndex = (index: number) => {
  activeIndex.value = index;
  resetAutoSwitch();
};

const focusTab = async (index: number) => {
  const tabCount = normalizedTabs.value.length;
  if (!tabCount) return;

  const nextIndex = (index + tabCount) % tabCount;
  activeIndex.value = nextIndex;
  resetAutoSwitch();
  await nextTick();
  document.getElementById(tabButtonId(nextIndex))?.focus();
};

const clearAutoSwitch = () => {
  if (!autoSwitchTimer) return;
  clearInterval(autoSwitchTimer);
  autoSwitchTimer = null;
};

const startAutoSwitch = () => {
  clearAutoSwitch();
  if (normalizedTabs.value.length <= 1) return;

  autoSwitchTimer = setInterval(() => {
    const tabCount = normalizedTabs.value.length;
    if (tabCount <= 1) {
      clearAutoSwitch();
      return;
    }

    activeIndex.value = (activeIndex.value + 1) % tabCount;
  }, autoSwitchInterval.value);
};

const resetAutoSwitch = () => {
  startAutoSwitch();
};

watch(
  () => normalizedTabs.value.length,
  (tabCount) => {
    if (activeIndex.value >= tabCount) activeIndex.value = 0;
    startAutoSwitch();
  }
);

onMounted(() => {
  startAutoSwitch();
});

onBeforeUnmount(() => {
  clearAutoSwitch();
});
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-tabs {
  overflow: hidden;
  background: $white;

  &__inner {
    width: 100%;
  }

  &__nav-wrap {
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__nav {
    position: relative;
    display: flex;
    gap: 12px;
    width: max-content;
    min-width: 100%;
    height: 50px;
    margin-left: -36px;
    padding: 0;
    background: transparent;

    &::before,
    &::after {
      content: '';
      flex: 0 0 72px;
      height: 100%;
      background: $color-growth-green;
      border-radius: 0 4px 0 4px;
      transform: skewX(-36deg);
    }
  }

  &__tab {
    position: relative;
    min-width: 160px;
    height: 50px;
    padding: 0 34px;
    border: 0;
    color: #6f9674;
    background: transparent;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    font-weight: 400;
    white-space: nowrap;
    transition: color 180ms ease;

    &:hover,
    &:focus-visible {
      color: #315b38;
      outline: none;

      .c-tabs__tab-bg {
        background: #e7eee6;
      }
    }

    &:focus-visible {
      .c-tabs__tab-bg {
        box-shadow: inset 0 0 0 2px #315b38;
      }
    }

    &.is-active {
      z-index: 2;
      color: $white;
      font-weight: 600;

      .c-tabs__tab-bg {
        background: $color-growth-green;
      }
    }
  }

  &__tab-bg {
    position: absolute;
    inset: 0;
    background: #f1f4f1;
    border-radius: 0 4px 0 4px;
    transform: skewX(-36deg);
    transition:
      background-color 180ms ease,
      box-shadow 180ms ease;
  }

  &__tab-text {
    position: relative;
    z-index: 1;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__panel {
    padding: 32px 24px 0;
  }

  @include tablet {
    &__nav {
      gap: 14px;
      width: max-content;
      min-width: 100%;
      height: 64px;
      margin-left: -44px;

      &::before,
      &::after {
        flex-basis: 88px;
      }
    }

    &__tab {
      min-width: 220px;
      height: 64px;
      padding: 0 40px 0 28px;
      font-size: 20px;
    }
  }

  @include tablet-landscape {
    &__nav-wrap {
      overflow: hidden;
    }

    &__nav {
      gap: 16px;
      width: calc(100% + 112px);
      min-width: 0;
      height: 72px;
      margin-left: -56px;

      &::before,
      &::after {
        flex: 0 0 120px;
        min-width: 92px;
      }
    }

    &__tab {
      flex: 1 1 0;
      min-width: 0;
      height: 72px;
      padding: 0 32px;
      font-size: 22px;
    }
  }
}
</style>
